import axios, { type AxiosResponse } from "axios";

import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { ApiResponse } from "@/services/api/types";
import { getRoleApiValue } from "@/utils/roleManager";

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
  lastLoggedIn: number;
  archived: boolean;
  allowedStores: number[];
  customerId: number;
  customerName: string;
}

export interface GetUsersParams {
  customerId?: number;
  storeId?: number;
  includeArchived?: boolean;
}

export class UserService extends BaseApiService {
  private static instance: UserService;

  private constructor() {
    super();
  }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getUsers(params?: GetUsersParams): Promise<ApiResponse<User[]>> {
    try {
      // Construct query string if params are provided
      const queryParams = params
        ? new URLSearchParams({
            ...(params.customerId ? { customerId: params.customerId.toString() } : {}),
            ...(params.storeId ? { storeId: params.storeId.toString() } : {}),
            ...(params.includeArchived !== undefined
              ? { includeArchived: params.includeArchived.toString() }
              : {}),
          }).toString()
        : "";

      const url = `/user${queryParams ? `?${queryParams}` : ""}`;
      const response = await this.get<{ data: User[] }>(url, this.getCoreBaseUrl());

      return {
        data: response.data,
        metadata: {
          code: 200,
          message: "Success",
        },
      };
    } catch (error) {
      throw error;
    }
  }

  public async getUserById(userId: number): Promise<ApiResponse<User>> {
    try {
      const response = await this.get<{ data: User }>(`/user/${userId}`, this.getCoreBaseUrl());
      return {
        data: response.data,
        metadata: {
          code: 200,
          message: "Success",
        },
      };
    } catch (error) {
      throw error;
    }
  }

  public async createUser(
    userData: Partial<User> & { password: string },
  ): Promise<ApiResponse<User>> {
    // Validate required fields
    if (!userData.firstName) {
      throw new Error("firstName must be provided.");
    }
    if (!userData.lastName) {
      throw new Error("lastName must be provided.");
    }
    if (!userData.username) {
      throw new Error("username must be provided.");
    }
    if (!userData.role) {
      throw new Error("role must be provided.");
    }
    if (!userData.password) {
      throw new Error("password must be provided.");
    }
    if (!userData.customerId) {
      throw new Error("customerId must be provided.");
    }

    // Only validate allowedStores for non-CUSTOMER_ADMIN roles
    if (
      userData.role !== "CUSTOMER_ADMIN" &&
      (!userData.allowedStores || userData.allowedStores.length === 0)
    ) {
      throw new Error("allowedStores must be provided for non-CUSTOMER_ADMIN roles.");
    }

    // Construct request body exactly matching API schema with required fields
    const requestBody = {
      firstName: userData.firstName.trim(),
      lastName: userData.lastName.trim(),
      username: userData.username.trim(),
      email: userData.email?.trim() || undefined,
      role: getRoleApiValue(userData.role),
      password: userData.password.trim(),
      customerId: userData.customerId,
      active: userData.active !== undefined ? userData.active : true,
      archived: userData.archived !== undefined ? userData.archived : false,
    };

    if (userData.allowedStores) {
      (requestBody as any).allowedStores = userData.allowedStores;
    }

    // Remove undefined values but keep explicit null values if set
    const cleanedRequestBody = Object.fromEntries(
      Object.entries(requestBody).filter(([_, v]) => v !== undefined),
    );

    return this.post<ApiResponse<User>>("/user", cleanedRequestBody, this.getCoreBaseUrl());
  }

  public async updateUser(id: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    // Validate required fields
    if (!userData.firstName) {
      throw new Error("firstName must be provided.");
    }
    if (!userData.lastName) {
      throw new Error("lastName must be provided.");
    }
    if (!userData.username) {
      throw new Error("username must be provided.");
    }
    if (!userData.role) {
      throw new Error("role must be provided.");
    }

    // Construct request body exactly matching API schema with required fields
    const requestBody = {
      id: parseInt(id),
      firstName: userData.firstName.trim(),
      username: userData.username.trim(),
      lastName: userData.lastName.trim(),
      role: getRoleApiValue(userData.role),
      active: userData.active,
      password: (userData as any).password?.trim() || undefined,
      changingRoomGroupId: (userData as any).changingRoomGroupId || undefined,
      notificationToken: (userData as any).notificationToken || undefined,
      allowedStores: userData.allowedStores || [],
      externalId: (userData as any).externalId || undefined,
    };

    // Remove undefined values but keep explicit null values if set
    const cleanedRequestBody = Object.fromEntries(
      Object.entries(requestBody).filter(([_, v]) => v !== undefined),
    );

    return this.put<ApiResponse<User>>("/user", cleanedRequestBody, this.getCoreBaseUrl());
  }

  public async archiveUser(id: string): Promise<ApiResponse<void>> {
    return this.put<ApiResponse<void>>(`/user/archive/${id}`, {}, this.getCoreBaseUrl());
  }

  public async generatePassword(): Promise<ApiResponse<string>> {
    return this.get<ApiResponse<string>>("/user/password", this.getCoreBaseUrl());
  }

  public async requestPasswordReset(email: string): Promise<AxiosResponse<ApiResponse<void>>> {
    return axios.post(`${this.getCoreBaseUrl()}/user/changePassword`, { email });
  }

  public async requestPasswordResetCode(email: string): Promise<AxiosResponse<ApiResponse<void>>> {
    return axios.post(`${this.getCoreBaseUrl()}/user/resetPassword/code`, { email });
  }

  public async resetPasswordWithCode(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<AxiosResponse<ApiResponse<string>>> {
    return axios.put(`${this.getCoreBaseUrl()}/user/resetPassword/code`, {
      email,
      code,
      newPassword,
    });
  }
}
